import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input, DatePicker, Spin, Empty, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fetchWipTracker } from './api';
import dayjs from 'dayjs';

const { Text } = Typography;

// ─── Lane definitions (matching wireframe) ──────────────────────────────────
const LANE_DEFS = [
  { key: 'Granulation', label: 'To Granulate', cls: 'gran',  color: '#15803d', bg: '#f0fdf4', border: '#22c55e' },
  { key: 'Blending',    label: 'To Blend',     cls: 'blend', color: '#7e22ce', bg: '#fdf4ff', border: '#a855f7' },
  { key: 'Compression', label: 'To Compress',  cls: 'comp',  color: '#c2410c', bg: '#fff7ed', border: '#f97316' },
  { key: 'Coating',     label: 'To Coat',      cls: 'coat',  color: '#b91c1c', bg: '#fef2f2', border: '#ef4444' },
  { key: 'Capsulation', label: 'To Capsulate', cls: 'cap',   color: '#b45309', bg: '#fffbeb', border: '#f59e0b' },
  { key: 'Imprinting',  label: 'To Imprint',   cls: 'imp',   color: '#2f54eb', bg: '#f0f5ff', border: '#597ef7' },
  { key: 'Packing',     label: 'To Pack',      cls: 'pack',  color: '#0f766e', bg: '#f0fdfa', border: '#14b8a6' },
];

const RANGE_PRESETS = [
  { label: '1W', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: 'All', days: 365 },
];

// ─── Status helpers ─────────────────────────────────────────────────────────
const getStatus = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return 'ok';
  if (daysLeft <= 0) return 'breach';
  if (daysLeft <= 7) return 'warning';
  return 'ok';
};

const STATUS_STYLES = {
  ok:      { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  warning: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  breach:  { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
};

const formatDaysLeft = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return '';
  if (daysLeft >= 0) return `${Math.round(daysLeft)}d left`;
  return `${Math.abs(Math.round(daysLeft))}d over`;
};

// ─── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, bgColor }) => (
  <div style={{
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '13px 16px', boxShadow: '0 1px 3px rgba(15,23,42,.06)', display: 'flex', alignItems: 'center', gap: 13,
  }}>
    <div style={{
      width: 38, height: 38, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 18, background: bgColor, flexShrink: 0,
    }}>{icon}</div>
    <div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: '#475569', fontWeight: 500 }}>{label}</div>
    </div>
  </div>
);

// ─── Batch Card ─────────────────────────────────────────────────────────────
const BatchCard = ({ batch }) => {
  const status = batch.status || getStatus(batch.days_left);
  const st = STATUS_STYLES[status] || STATUS_STYLES.ok;
  const daysText = formatDaysLeft(batch.days_left);
  const hasInterstage = batch.hold_limit_days !== null && batch.hold_limit_days !== undefined;
  const overarchingLimit = batch.overarching_limit_days;
  const missingTransition = !hasInterstage && batch.previous_stage && batch.stage;

  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
      padding: '9px 11px', boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: '#1e3a5f', marginBottom: 3 }}>
        {batch.batch_number}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 500 }}>
          {batch.product_name || batch.product_code}
          {batch.volume != null && (
            <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4, fontFamily: "'JetBrains Mono', monospace" }}>
              {batch.volume}M
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {overarchingLimit != null ? (
            <span style={{
              fontSize: 8.5, color: '#64748b', fontFamily: "'JetBrains Mono', monospace",
              background: '#f1f5f9', padding: '1px 5px', borderRadius: 4,
            }}>
              Cycle {overarchingLimit}d
            </span>
          ) : (
            <span style={{ fontSize: 8, color: '#dc2626', fontStyle: 'italic' }}>
              No rule
            </span>
          )}
          {missingTransition && (
            <span
              title={`Missing interstage rule: ${batch.previous_stage} → ${batch.stage} for ${batch.product_code}. Add rule in Master Rules.`}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 14, height: 14, borderRadius: '50%',
                background: '#fef2f2', border: '1px solid #fecaca',
                color: '#dc2626', fontSize: 9, fontWeight: 700, cursor: 'help',
                flexShrink: 0,
              }}
            >
              !
            </span>
          )}
        </div>
      </div>
      {hasInterstage && daysText && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
          <div style={{
            fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
            fontFamily: "'JetBrains Mono', monospace", display: 'inline-block',
            background: st.bg, color: st.color, border: `1px solid ${st.border}`,
          }}>
            {daysText}
          </div>
          <span style={{ fontSize: 8.5, color: '#94a3b8', fontStyle: 'italic' }}>
            {batch.previous_stage}→ {batch.hold_limit_days}d
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Lane Column ────────────────────────────────────────────────────────────
const Lane = ({ laneDef, batches }) => {
  const totalVol = batches.reduce((s, b) => s + (b.volume || 0), 0);
  return (
  <div style={{
    width: 215, flexShrink: 0, background: '#f8fafc',
    border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden',
  }}>
    <div style={{
      padding: '9px 12px', borderBottom: `2px solid ${laneDef.border}`,
      background: laneDef.bg, color: laneDef.color,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {laneDef.label}
        </span>
        <span style={{
          background: 'rgba(255,255,255,.85)', borderRadius: 8, padding: '1px 7px',
          fontSize: 10, fontWeight: 700, color: laneDef.color,
        }}>
          {batches.length}
        </span>
      </div>
      {totalVol > 0 && (
        <div style={{ fontSize: 9, fontWeight: 500, opacity: 0.8, marginTop: 1 }}>
          Vol: {totalVol.toFixed(1)}M
        </div>
      )}
    </div>
    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, minHeight: 40 }}>
      {batches.map(b => <BatchCard key={b.batch_number} batch={b} />)}
    </div>
  </div>
  );
};

// ─── Legend ──────────────────────────────────────────────────────────────────
const LegendDot = ({ bg, border, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#475569' }}>
    <div style={{ width: 10, height: 10, borderRadius: 2, background: bg, border: `1px solid ${border}` }} />
    {label}
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const WipTracker = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [activePreset, setActivePreset] = useState('3M');
  const [fromDate, setFromDate] = useState(() => dayjs().subtract(90, 'day'));
  const [toDate, setToDate] = useState(() => dayjs());

  const loadData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const result = await fetchWipTracker({
        from_date: from.format('YYYY-MM-DD'),
        to_date: to.format('YYYY-MM-DD'),
      });
      setData(result);
    } catch (err) {
      console.error('WipTracker load error:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(fromDate, toDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    const to = dayjs();
    const from = dayjs().subtract(preset.days, 'day');
    setFromDate(from);
    setToDate(to);
    loadData(from, to);
  };

  const handleDateChange = (type, val) => {
    if (!val) return;
    setActivePreset(null);
    if (type === 'from') {
      setFromDate(val);
      loadData(val, toDate);
    } else {
      setToDate(val);
      loadData(fromDate, val);
    }
  };

  // Build lane data from API response
  const lanes = useMemo(() => {
    if (!data?.lanes) return [];
    return data.lanes;
  }, [data]);

  // Filter by search
  const filteredLanes = useMemo(() => {
    if (!search.trim()) return lanes;
    const q = search.trim().toLowerCase();
    return lanes.map(lane => ({
      ...lane,
      batches: lane.batches.filter(b =>
        (b.batch_number || '').toLowerCase().includes(q) ||
        (b.product_name || '').toLowerCase().includes(q) ||
        (b.product_code || '').toLowerCase().includes(q)
      ),
    }));
  }, [lanes, search]);

  // Summary stats
  const summary = useMemo(() => {
    if (data?.summary) return data.summary;
    const allBatches = filteredLanes.flatMap(l => l.batches);
    return {
      total_active: allBatches.length,
      ok_count: allBatches.filter(b => (b.status || getStatus(b.days_left)) === 'ok').length,
      warning_count: allBatches.filter(b => (b.status || getStatus(b.days_left)) === 'warning').length,
      breached_count: allBatches.filter(b => (b.status || getStatus(b.days_left)) === 'breach').length,
    };
  }, [data, filteredLanes]);

  // Check if any lanes have data
  const hasData = filteredLanes.some(l => l.batches.length > 0);

  return (
    <div>
      {/* Filter bar */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        padding: '9px 16px', marginBottom: 14, display: 'flex', alignItems: 'center',
        gap: 10, flexWrap: 'wrap', boxShadow: '0 1px 3px rgba(15,23,42,.06)',
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#94a3b8' }}>From</span>
        <DatePicker value={fromDate} onChange={v => handleDateChange('from', v)} size="small" style={{ width: 120 }} />
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#94a3b8' }}>To</span>
        <DatePicker value={toDate} onChange={v => handleDateChange('to', v)} size="small" style={{ width: 120 }} />
        <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />
        <div style={{ display: 'flex', gap: 2 }}>
          {RANGE_PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => handlePreset(p)}
              style={{
                padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 5,
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                background: activePreset === p.label ? '#2563eb' : '#f8fafc',
                color: activePreset === p.label ? '#fff' : '#475569',
                borderColor: activePreset === p.label ? '#2563eb' : '#e2e8f0',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />
        <Input
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search batch or product..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 210 }}
          size="small"
          allowClear
        />
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <KpiCard icon="📦" value={summary.total_active} label="Active Batches" bgColor="#eff6ff" />
        <KpiCard icon="🚨" value={summary.breached_count} label="Hold Breached" bgColor="#fef2f2" />
        <KpiCard icon="⚠" value={summary.warning_count} label="Expiring Within 7 Days" bgColor="#fffbeb" />
        <KpiCard icon="✓" value={summary.ok_count} label="Within Hold Time Limits" bgColor="#f0fdf4" />
      </div>

      {/* Board card */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 3px rgba(15,23,42,.06)', overflow: 'hidden',
      }}>
        {/* Card header */}
        <div style={{
          padding: '11px 16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            WIP Tracker — Batch Location by Stage
            <span style={{
              fontSize: 11, fontWeight: 700, background: '#2563eb', color: '#fff',
              padding: '2px 8px', borderRadius: 10,
            }}>
              {summary.total_active}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <LegendDot bg="#f0fdf4" border="#bbf7d0" label=">7 days left" />
            <LegendDot bg="#fffbeb" border="#fde68a" label="≤7 days" />
            <LegendDot bg="#fef2f2" border="#fecaca" label="Breached" />
          </div>
        </div>

        {/* Kanban board */}
        <div style={{ overflowX: 'auto', padding: '16px 16px 20px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : !hasData ? (
            <Empty
              description={<Text type="secondary">No active batches match current filter.</Text>}
              style={{ padding: 40 }}
            />
          ) : (
            <div style={{ display: 'flex', gap: 14, minWidth: 'max-content', alignItems: 'flex-start' }}>
              {LANE_DEFS.map(laneDef => {
                const lane = filteredLanes.find(l => l.stage === laneDef.key);
                const batches = lane?.batches || [];
                if (batches.length === 0) return null;
                return <Lane key={laneDef.key} laneDef={laneDef} batches={batches} />;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WipTracker;
